import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserPlus } from 'lucide-react';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { sendAdminNewUserEmail } from '../../lib/api/emailNotifications';
import { useEmbedStore } from '../../lib/embedStore';
import { isTslhTenant } from '../../lib/tenantStore';
import { TSLH_LEAGUE_ID } from '../../lib/tenantSession';
import { joinTslhLeagueAndRedirect } from '../../lib/tenantJoin';
import { useUserStore } from '../../lib/userStore';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function RegisterForm() {
  const navigate = useNavigate();
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const setUsername = useUserStore((state) => state.setUsername);
  const setUserId = useUserStore((state) => state.setUserId);
  const setDateOfBirthState = useUserStore((state) => state.setDateOfBirth);
  const [loading, setLoading] = useState(false);
  const { language } = useLanguageStore();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
  });
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1900 + 1 }, (_, i) => currentYear - i);
  const months = language === 'fr' ? MONTHS_FR : MONTHS_EN;

  const daysInMonth = birthMonth
    ? new Date(birthYear ? parseInt(birthYear) : 2000, parseInt(birthMonth), 0).getDate()
    : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const isBirthComplete = birthDay && birthMonth && birthYear;

  const handleBirthMonthChange = (newMonth: string) => {
    setBirthMonth(newMonth);
    if (birthDay) {
      const maxDays = newMonth
        ? new Date(birthYear ? parseInt(birthYear) : 2000, parseInt(newMonth), 0).getDate()
        : 31;
      if (parseInt(birthDay) > maxDays) setBirthDay('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isBirthComplete) {
      toast.error(translate('date.of.birth.required', language));
      return;
    }

    const dateOfBirth = `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`;

    // Valider que la date n'est pas dans le futur
    const selectedDate = new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay));
    if (selectedDate > new Date()) {
      toast.error(language === 'fr' ? 'La date ne peut pas être dans le futur' : 'Date cannot be in the future');
      return;
    }

    setLoading(true);

    try {
      // Trim any trailing spaces from the username
      const trimmedUsername = formData.username.replace(/\s+$/, '');

      const { data, error } = await supabase.rpc('create_user', {
        p_username: trimmedUsername,
        p_email: formData.email,
        p_password: formData.password,
        p_date_of_birth: dateOfBirth || null,
        p_context_league_id: isTslhTenant() ? TSLH_LEAGUE_ID : null,
      });

      // If the function returns a string indicating email exists, show a specific error
      if (typeof data === 'string' && data.toLowerCase().includes('email') && data.toLowerCase().includes('déjà')) {
        toast.error(translate('registration.email.exists', language) || "Un compte avec cet email existe déjà.");
        return;
      }

      if (error) throw error;

      const shouldNotifyAdminNewUser = !isEmbed && !isTslhTenant();

      // Notify admin (non-blocking)
      if (shouldNotifyAdminNewUser) {
        try {
          await sendAdminNewUserEmail({ username: trimmedUsername, email: formData.email });
        } catch (emailErr) {
          console.error('Admin new user email failed (non-blocking):', emailErr);
        }
      }

      toast.success(translate('account.created', language));

      if (isTslhTenant()) {
        // create_user ne retourne pas le user_id (TEXT inchangé pour BPM) : on relit le compte via login_user
        const { data: loginData, error: loginError } = await supabase.rpc('login_user', {
          p_email: formData.email,
          p_password: formData.password,
          p_context_league_id: TSLH_LEAGUE_ID,
        });
        const newUser = Array.isArray(loginData) && loginData.length > 0 ? loginData[0] : null;
        if (loginError || !newUser?.user_id) {
          console.error('TSLH post-signup lookup error:', loginError);
          toast.error(translate('registration.error', language));
          return;
        }

        const { error: joinError } = await supabase.rpc('add_user_to_league', {
          p_user_id: newUser.user_id,
          p_league_id: TSLH_LEAGUE_ID,
        });
        if (joinError) {
          console.error('TSLH join error:', joinError);
          toast.error('Erreur lors de la mise en place de ta ligue. Réessaie.');
          return;
        }

        setUsername(newUser.username);
        setUserId(newUser.user_id);
        setDateOfBirthState(newUser.date_of_birth || null);
        await joinTslhLeagueAndRedirect(newUser.user_id, navigate);
        return;
      }

      navigate(isEmbed ? '/standings' : '/my-office');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : undefined;
      toast.error(message || translate('registration.error', language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <div className="text-center mb-4">
          <UserPlus className="h-12 w-12 text-primary mb-2" />
          {/* <h2 className="card-title text-center mb-4">{translate('join.pool', language)}</h2> */}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">{translate('username', language)}</label>
            <input
              type="text"
              className="form-control"
              id="username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              pattern="[a-zA-Z0-9\s]+"
              title="Lettres, chiffres et espaces uniquement"
            />
          </div>

          <div className="mb-3">
            <label htmlFor="email" className="form-label">{translate('email.address', language)}</label>
            <input
              type="email"
              className="form-control"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label">{translate('date.of.birth', language)} *</label>
            <div className="row g-2 gx-1 gx-sm-2">
              {/* Jour */}
              <div className="col-4">
                <label className="form-label small text-muted mb-1 d-block text-center text-nowrap">
                  {language === 'fr' ? 'Jour' : 'Day'}
                </label>
                <select
                  className="form-select text-center px-1 px-sm-2"
                  value={birthDay}
                  onChange={(e) => setBirthDay(e.target.value)}
                  required
                  style={{ fontSize: 'clamp(0.85rem, 3.5vw, 1.1rem)' }}
                >
                  <option value="">--</option>
                  {days.map((d) => (
                    <option key={d} value={String(d)}>{d}</option>
                  ))}
                </select>
              </div>
              {/* Mois */}
              <div className="col-4">
                <label className="form-label small text-muted mb-1 d-block text-center text-nowrap">
                  {language === 'fr' ? 'Mois' : 'Month'}
                </label>
                <select
                  className="form-select text-center px-1 px-sm-2"
                  value={birthMonth}
                  onChange={(e) => handleBirthMonthChange(e.target.value)}
                  required
                  style={{ fontSize: 'clamp(0.85rem, 3.5vw, 1.1rem)' }}
                >
                  <option value="">--</option>
                  {months.map((m, i) => (
                    <option key={i + 1} value={String(i + 1)}>{m}</option>
                  ))}
                </select>
              </div>
              {/* Année */}
              <div className="col-4">
                <label className="form-label small mb-1 d-block text-center fw-bold text-nowrap" style={{ color: 'var(--bs-primary)' }}>
                  {language === 'fr' ? 'Année' : 'Year'}
                </label>
                <select
                  className="form-select text-center px-1 px-sm-2"
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  required
                  style={{ fontSize: 'clamp(0.85rem, 3.5vw, 1.1rem)', borderColor: 'var(--bs-primary)', borderWidth: 2 }}
                >
                  <option value="">----</option>
                  {years.map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            {!birthYear && (
              <div className="mt-1 text-center">
                <small className="text-warning">
                  {language === 'fr'
                    ? 'Sélectionnez votre année de naissance dans la liste ci-dessus'
                    : 'Select your birth year from the list above'}
                </small>
              </div>
            )}
            {isBirthComplete && (
              <div className="mt-2 p-2 rounded text-center" style={{ backgroundColor: 'rgba(var(--bs-success-rgb), 0.1)' }}>
                <small className="text-success fw-semibold">
                  ✓{' '}
                  {language === 'fr'
                    ? `${birthDay} ${months[parseInt(birthMonth) - 1]} ${birthYear}`
                    : `${months[parseInt(birthMonth) - 1]} ${birthDay}, ${birthYear}`}
                </small>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="password" className="form-label">{translate('password', language)}</label>
            <input
              type="password"
              className="form-control"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? translate('creating.account', language) : translate('create.account', language)}
          </button>
        </form>
      </div>
    </div>
  );
}